import { workspaceFeatureMounted } from './workspace-route-policy';

type ConnectionHint = Readonly<{ saveData?: boolean; effectiveType?: string }>;

const promises = new Map<string, Promise<unknown>>();
const once = <T>(key: string, load: () => Promise<T>): Promise<T> => {
  const existing = promises.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const pending = load().catch((error) => {
    promises.delete(key);
    throw error;
  });
  promises.set(key, pending);
  return pending;
};

export const defineFeatureLoader =
  <T>(key: string, load: () => Promise<T>) =>
  () =>
    once(key, load);

export const loadInspectionWorkflowModule = defineFeatureLoader(
  'inspection-workflow',
  () => import('./features/inspection/inspection-workflow'),
);
export const loadInspectionDashboardModule = defineFeatureLoader(
  'inspection-dashboard',
  () => import('./features/inspection-read-model/inspection-read-workspace'),
);
export const loadAccountSettingsModule = defineFeatureLoader(
  'account-settings',
  () => import('./features/account-settings/account-settings-workspace'),
);

export function routePrefetchAllowed(connection?: ConnectionHint | null) {
  return !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType ?? '');
}

const directRouteLoader = (featureId: string): (() => Promise<unknown>) | null => {
  if (!workspaceFeatureMounted(featureId)) return null;
  if (featureId === 'inspection-log') return loadInspectionWorkflowModule;
  if (
    ['inspection-reporting', 'inspection-unresolved'].includes(
      featureId,
    )
  )
    return loadInspectionDashboardModule;
  if (featureId === 'site-settings') return loadAccountSettingsModule;
  return null;
};

function connection(): ConnectionHint | null {
  if (typeof navigator === 'undefined') return null;
  return (navigator as Navigator & { connection?: ConnectionHint }).connection ?? null;
}

export function prefetchFeatureRoute(featureId: string) {
  if (!routePrefetchAllowed(connection()) || !workspaceFeatureMounted(featureId)) return;
  const direct = directRouteLoader(featureId);
  if (direct) {
    void direct().catch(() => undefined);
    return;
  }
  void import('./feature-prefetch-registry')
    .then(({ prefetchRegisteredFeature }) => prefetchRegisteredFeature(featureId))
    .catch(() => undefined);
}

export function prefetchEntitledRoutes(featureIds: readonly string[]) {
  if (!routePrefetchAllowed(connection())) return;
  for (const featureId of new Set(featureIds)) prefetchFeatureRoute(featureId);
}
